/*
 * layout.js
 * ---------------
 * Exports the layout component.
 */

import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import Header from "./header";
import Footer from "./footer/footer";
import NavigationDrawer from "./navigation-drawer";
import WhatsappButton from "./whatsapp-button";
import styles from "./layout.module.scss";
import { defaultLang } from "../constants";
import { useTranslation } from "../hooks/useTranslation";
import { useIntl } from "../hooks/useIntl";
import { getIntlPagePath } from "../utils/getIntlPagePath";
import markdownPagePaths from "../markdownPagePaths.json";

const Layout = ({ children, headerTakesSpace }) => {
  const [navIsOpen, setNavIsOpen] = useState(false);

  const { lang } = useIntl();
  const t = useTranslation();
  const tComponent = useTranslation("components.Layout");

  const navLinks = useMemo(() => [
    {
      to: getIntlPagePath(t("pages.index.path"), lang),
      text: tComponent("text.homeLink"),
    },
    ...(lang === defaultLang ? [{
      to: "/appuntamenti/",
      text: tComponent("text.bookAppointmentLink"),
    }] : []),
    {
      to: markdownPagePaths.azienda[lang],
      text: tComponent("text.companyLink"),
    },
    {
      to: markdownPagePaths.showroom[lang],
      text: tComponent("text.showroomLink"),
    },
    {
      to: markdownPagePaths.prodotti[lang],
      text: tComponent("text.productsLink"),
    },
    {
      to: markdownPagePaths["servizio-manutenzione"][lang],
      text: tComponent("text.maintenanceService"),
    },
    {
      to: markdownPagePaths["chiavi-in-mano"][lang],
      text: tComponent("text.turnkeyFormulaLink"),
    },
    {
      to: markdownPagePaths["lavora-con-noi"][lang],
      text: tComponent("text.workWithUsLink"),
    },
    {
      to: `${getIntlPagePath(t("pages.index.path"), lang)}#${t("components.Footer.ids.contact")}`,
      text: tComponent("text.contactLink"),
    },
  ], [t, lang, tComponent]);

  const handleNavClose = useCallback(() => {
    setNavIsOpen(false);
  }, []);

  const openNav = useCallback(() => {
    setNavIsOpen(true);
  }, []);

  return (
    <>
      <NavigationDrawer
        navLinks={navLinks}
        isOpen={navIsOpen}
        onClose={handleNavClose}
      />

      <div className={styles.container}>
        <div className={styles.content}>
          <Header
            headerTakesSpace={headerTakesSpace}
            navLinks={navLinks}
            navWider={lang === defaultLang}
            onMenuButtonClick={openNav}
          />
          { children }
        </div>

        <Footer />
      </div>

      <WhatsappButton />
    </>
  );
};

Layout.defaultProps = {
  headerTakesSpace: true,
};

Layout.propTypes = {
  headerTakesSpace: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export default Layout;
